import { validate } from 'app_vodafone_ziggo/util/validation';
import * as debounce from 'lodash/debounce';

/**
 * Initializes the "Let's Talk" functionality for appointment scheduling
 * @param {HTMLElement} modal - The modal element containing the appointment form
 * @returns {Function} A cleanup function to remove event listeners
 */
export default function initLetsTalk(modal) {
    const spinner = document.querySelector('vz-spinner') || new window.VZSpinner();
    const day = modal.querySelector('.js-appointment-day');
    const timeSlot = modal.querySelector('.js-appointment-time');
    const employeesField = modal.querySelector('.js-lead-form-employees');

    day.setAttribute('disabled', true);

    /**
     * Resets and disables the time slot selector
     */
    function resetTimeSlot() {
        const selectAppointmentTimeElement = timeSlot.querySelector('.js-select-appointment-time');
        if (selectAppointmentTimeElement) {
            [...selectAppointmentTimeElement.options].filter((option) => !option.hasAttribute('disabled')).forEach((option) => option.remove());
            selectAppointmentTimeElement.classList.remove('invalid', 'valid');
            selectAppointmentTimeElement.setAttribute('disabled', true);

            const timeSlotErrorMessageElement = selectAppointmentTimeElement.parentElement.querySelector('.error-msg');
            if (timeSlotErrorMessageElement) timeSlotErrorMessageElement.remove();
        }
    }

    /**
     * Handles the date change event
     * @param {Event} event - The change event object
     */
    function handleChangeDate(event) {
        const value = `${event.detail.date.getFullYear()}-${event.detail.date.getMonth() + 1}-${event.detail.date.getDate()}`;
        const {
            target: {
                dataset: { url, resourceId, cgid }
            }
        } = event;
        const employeesValue = employeesField.value.trim();
        const urlWithParams = `${url}${value}${employeesValue ? `&employees=${encodeURIComponent(employeesValue)}` : ''}${resourceId ? `&resourceId=${encodeURIComponent(resourceId)}` : ''}&cgid=${encodeURIComponent(cgid)}`;
        const dayErrorMessageElement = day.parentElement.querySelector('.error-msg');

        resetTimeSlot();
        spinner.start();

        fetch(urlWithParams)
            .then((response) => {
                if (response.status === 200 && dayErrorMessageElement) dayErrorMessageElement.remove();
                return response.text();
            })
            .then((response) => {
                timeSlot.innerHTML = response;
            })
            .finally(spinner.stop);
    }

    /**
     * Updates the appointment date section based on employee count
     * @param {string} employeesValue - Number of employees entered
     * @param {string} resourceId - Resource ID for the predefined sales agent
     * @param {string} cgid - Category ID used for territory Id's
     * @returns {Promise} Promise that resolves when the update is complete
     */
    function handleEmployeesDateUpdate(employeesValue, resourceId, cgid) {
        const dateSection = modal.querySelector('.js-appointment-date');
        const url = employeesField.dataset.url;
        const urlWithParams = `
            ${url}${employeesValue ? `?employees=${encodeURIComponent(employeesValue)}` : ''}
            ${resourceId ? `&resourceId=${encodeURIComponent(resourceId)}` : ''}
            ${cgid ? `&cgid=${encodeURIComponent(cgid)}` : ''}
        `.replace(/\s+/g, '');

        spinner.start();

        return fetch(urlWithParams)
            .then((response) => response.text())
            .then((response) => {
                const currentDateField = modal.querySelector('.js-appointment-day');
                const wasDatePickerOpen = currentDateField && currentDateField.parentElement.querySelector('.datepicker.active') !== null;

                dateSection.innerHTML = response;
                const newDateField = dateSection.querySelector('.js-appointment-day');
                if (newDateField) {
                    newDateField.addEventListener('changeDate', handleChangeDate);
                    if (wasDatePickerOpen) newDateField.focus();
                }
            })
            .finally(spinner.stop);
    }

    /**
     * Resets and disables the appointment date field
     */
    function resetAppointmentDate() {
        handleEmployeesDateUpdate('', '', '').then(() => {
            const newDateField = modal.querySelector('.js-appointment-day');

            if (newDateField) {
                newDateField.setAttribute('disabled', true);
                newDateField.classList.remove('invalid', 'valid');
                newDateField.classList.add('empty');
                newDateField.value = '';
            }
        });
    }

    /**
     * Checks if the new employee count crosses the threshold in either direction
     * @param {string} oldValue - Previous value from input field
     * @param {string} newValue - New value from input field
     * @param {number} threshold - Threshold value to compare against
     * @returns {boolean} True if threshold is crossed in either direction
     */
    function hasPassedThreshold(oldValue, newValue, threshold) {
        const oldCount = parseInt(oldValue || '0', 10);
        const newCount = parseInt(newValue || '0', 10);

        if (oldCount === 0) return true;
        if (threshold === 0) return false;

        const wasBelow = oldCount < threshold;
        const isNowBelow = newCount < threshold;

        return wasBelow !== isNowBelow;
    }

    /**
     * Handles employee field changes
     * @param {Event} event - The input event object
     */
    function handleEmployeeChange(event) {
        const value = event.target.value.trim();
        const oldValue = event.target.dataset.lastValue || '0';
        const threshold = parseInt(event.target.dataset.employeeThreshold, 10);
        const surpassedThreshold = hasPassedThreshold(oldValue, value, threshold);
        const resourceId = event.target.dataset.resourceId;
        const cgid = event.target.dataset.cgid;

        if (value === '' || Number.isNaN(parseInt(value, 10))) return;
        if (value === oldValue) return;

        event.target.dataset.lastValue = value;

        if (surpassedThreshold) {
            if (value) {
                handleEmployeesDateUpdate(value, resourceId, cgid).then(() => {
                    day.removeAttribute('disabled');

                    const newDateField = modal.querySelector('.js-appointment-day');
                    if (newDateField && oldValue !== '0') {
                        validate({ target: newDateField }, newDateField.dataset.surpassThresholdError);
                    }
                });

                resetTimeSlot();
            } else {
                resetAppointmentDate();
                resetTimeSlot();
            }
        } else if (value && value === '0') {
            resetAppointmentDate();
            resetTimeSlot();
        }
    }

    const debouncedHandleEmployeeChange = debounce(handleEmployeeChange, 500);

    day.addEventListener('changeDate', handleChangeDate);
    employeesField.addEventListener('keyup', debouncedHandleEmployeeChange);

    return () => {
        day.removeEventListener('changeDate', handleChangeDate);
        employeesField.removeEventListener('keyup', debouncedHandleEmployeeChange);
        employeesField.removeAttribute('data-last-value');
    };
}
